import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useNavigate } from 'react-router-dom';
import { useEnhancedAuth } from '@/contexts/AuthContext';
import { logAnalyticsEvent, ANALYTICS_EVENTS } from '@/utils/Analytics';
import TryAQuestion from '@/pages/TryAQuestion';
import { LandingValueSections } from '@/components/home/LandingValueSections';
import { diagnosticVariant } from '@/utils/diagnosticVariant';
import { DiagnosticPrerender, NEUTRAL_DIAGNOSTIC_HEADLINE, PublicDiagnosticShell } from '@/features/publicDiagnostic';
import { useArrivedSignedIn } from '@/hooks/usePublicFunnelGate';

/**
 * The homepage — diagnostic-first, for ALL traffic (not gated behind ?v=b).
 *
 * Strategic call (2026-07): traffic is tiny, so this isn't a leaky-funnel
 * optimization problem — the diagnostic (`/try`) already converts once started.
 * The move is to stop hiding the best version of the homepage behind a fake A/B
 * test. So `/` now renders the SAME diagnostic engine as `/try` (reused, not
 * duplicated — see TryAQuestion.tsx).
 *
 * The old A/B gate (`abVariant('landing_v1')`) is gone — there's one homepage.
 * A `?v=b` visitor (the one campaign link that used to force the old variant B)
 * still lands here safely; it's simply inert now.
 *
 * Corrected 2026-07-08 (founder + Fable review): the first cut of this page led
 * with race/identity framing as the universal above-the-fold headline and
 * invented new first-person copy attributed to Pam (incl. a "wrote" claim —
 * violates the hard rule that she REVIEWED, never "wrote," the questions; see
 * memory `pam-persona-identity`). That's a channel-targeting insight (the
 * documented Black-retaker pass-rate gap), not universal homepage positioning —
 * it belongs on the targeted outreach channels it was researched for (NABSW,
 * HBCU career services, etc.), not 100% of mixed traffic, and nothing in Pam's
 * voice ships without her actual sign-off. Removed here; copy is credential-based
 * (reviewed by a practicing LCSW), not identity-based.
 */

const structuredData = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'Social Work Test Prep Academy',
  url: 'https://www.socialworktestprepacademy.com',
  description:
    'ASWB exam prep with adaptive practice questions reviewed by a licensed clinical social worker, a free diagnostic, and AI-powered clinical simulations.',
};

const HOME_TITLE = NEUTRAL_DIAGNOSTIC_HEADLINE;
const HOME_OG_TITLE = 'Pass Your ASWB Exam | Social Work Test Prep Academy';
const HOME_OG_IMAGE = 'https://www.socialworktestprepacademy.com/images/home-og-image.png';
const SIX_TURN_META_DESCRIPTION =
  'Pass your ASWB exam with adaptive practice, a clinical simulation beat, spaced flashcards, and Ask Pam. No account to start.';

const SIX_TURNS = ['Question', 'Follow-up', 'Simulation', 'Timed', 'Flashcard', 'Ask Pam'];

// .pen "Home Hero — Six Turns". Promises only what the six turns deliver: no score, no emailed plan.
export const SixTurnHero: React.FC = () => (
  <div
    className="mb-4 rounded-2xl bg-primary-500 shadow-ledge-brand px-5 py-5 sm:p-8 text-white animate-fade-in"
    data-testid="six-turn-home-hero"
  >
    <p className="eyebrow text-primary-200 mb-2 sm:mb-3">Free · no account · reviewed by a practicing LCSW</p>
    <h1 className="font-display font-bold text-white text-[26px] sm:text-4xl leading-[1.1] mb-2 sm:mb-3">
      Try six real ASWB moments, free.
    </h1>
    {/* Phones get the short line and no chips, so turn 1's first answer stays above the fold at 375x667. */}
    <p className="text-primary-100 text-[15px] leading-snug sm:hidden">
      Six real moments. Questions reviewed by Pam Coin, LCSW. No account needed.
    </p>
    <p className="hidden sm:block text-primary-100 text-base leading-relaxed">
      A question that adapts to you, a clinical simulation, a timed question, a flashcard, and a question for
      Pam. Questions reviewed by Pam Coin, LCSW. No account needed.
    </p>
    <ol aria-label="The six turns" className="mt-4 hidden sm:grid grid-cols-6 gap-2">
      {SIX_TURNS.map((turn) => (
        <li
          key={turn}
          className="rounded-full border-2 border-white/40 px-2 py-1.5 text-center text-xs font-bold whitespace-nowrap text-white/80 shadow-[0_3px_0_0_rgb(var(--color-primary-700))]"
        >
          {turn}
        </li>
      ))}
    </ol>
    <p className="mt-4 hidden sm:block text-sm font-bold text-celebrate-200">Start below ↓</p>
  </div>
);

/**
 * The empty "readiness gauge" that anchors the hero — an instrument waiting for a
 * number, not a claimed score. A faint white track (the dial), a single gold tick
 * at 12 o'clock (the credential seed + a visible starting point), and a "?" where
 * the real readiness number lands after the diagnostic. This foreshadows the
 * filled ring the diagnostic pays off with (see .pen "Readiness Hero" / the report
 * stage), so the page reads as *about to measure you* rather than as an empty form
 * — the endowed-progress effect, without faking a score.
 */
const EmptyReadinessGauge: React.FC = () => {
  // One drawing, two sizes: the box is 96px on phones and the .pen's 156px from
  // sm up. The SVG scales through its viewBox, so stroke weight, the gold tick
  // and the ring proportions are identical at both sizes.
  const size = 156;
  const stroke = 13;
  const r = (size - stroke) / 2;
  const c = size / 2;
  const circ = 2 * Math.PI * r;
  return (
    <div
      className="relative flex-shrink-0 w-24 h-24 sm:w-[156px] sm:h-[156px]"
      aria-hidden="true"
    >
      <svg className="w-full h-full" viewBox={`0 0 ${size} ${size}`}>
        {/* The dial: a faint, complete track — the space your number will fill */}
        <circle cx={c} cy={c} r={r} fill="none" stroke="rgba(255,255,255,0.16)" strokeWidth={stroke} />
        {/* The starting tick at 12 o'clock, in credential gold — a visible start point */}
        <circle
          cx={c}
          cy={c}
          r={r}
          fill="none"
          stroke="#F5B53D"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`3 ${circ}`}
          transform={`rotate(-90 ${c} ${c})`}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-display font-bold text-white leading-none text-[2.25rem] sm:text-[3.25rem]">
          ?
        </span>
        <span className="mt-1 text-[10px] font-extrabold uppercase tracking-[0.15em] text-white/60">
          Exam ready
        </span>
      </div>
    </div>
  );
};

// The hero is the intro card's fixed brand-teal top zone (.pen "Readiness Hero"):
// full-bleed (the card wrapper owns the border, rounding + ledge), pinned to the
// exact .pen hexes (--brand #2C5F5D fill) as a literal — NOT the primary-* token,
// which theme-flips to a light teal in dark mode and would invert this white-on-
// teal treatment.
// Phones get the same hero, compacted: tighter padding/rhythm, a 26px headline,
// the 96px dial, and no "scroll down" pointer, because after this change the exam
// picker and the start button are already on screen (see the PR's measurements).
// Every override reverts at sm, so tablets and desktop render exactly as before.
const HeroSection: React.FC = () => (
  <div
    className="px-5 py-5 sm:p-7 md:p-8 animate-fade-in"
    style={{ backgroundColor: '#2C5F5D' }}
  >
    <p className="eyebrow text-white/70 mb-2 sm:mb-4">Free · no account · reviewed by a practicing LCSW</p>
    <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-6 md:gap-8">
      <div className="flex-1 text-center sm:text-left">
        <h1 className="font-display font-bold text-white text-[26px] sm:text-3xl md:text-4xl leading-[1.1] mb-2 sm:mb-3">
          See exactly where you stand on the ASWB.
        </h1>
        <p className="text-white/80 text-[15px] leading-snug sm:text-base sm:leading-relaxed">
          Take the free 6-question diagnostic and get your readiness score in 3 minutes: your
          weakest areas and the exact thing to study next, with your Pass Plan sent to your inbox. Every question reviewed by Pam Coin, LCSW.
        </p>
        <p className="text-white/60 text-sm leading-snug sm:leading-5 mt-2">
          Taken it before? Let&rsquo;s find <em>why</em>, not just <em>that</em>, you missed.
        </p>
      </div>
      <EmptyReadinessGauge />
    </div>
    <p className="hidden sm:block sm:mt-6 text-center sm:text-left text-sm font-bold" style={{ color: '#F8D991' }}>
      Take the 6-question diagnostic below to reveal your number ↓
    </p>
    {/* The question people ask BEFORE prep: which license, which exam, in what
        order. One line into the state pages, never competing with the CTA. */}
    <p className="mt-3 text-center sm:text-left text-sm text-white/70">
      Not sure what your state requires first?{' '}
      <Link to="/licensing" className="font-bold text-white underline">
        See your state.
      </Link>
    </p>
  </div>
);

const Landing: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser, isAuthLoading, isStudentViewActive } = useEnhancedAuth();

  useEffect(() => {
    logAnalyticsEvent(ANALYTICS_EVENTS.LANDING_PAGE_VIEWED, {
      page_name: 'home',
      timestamp: new Date().toISOString(),
    });
  }, []);

  // Send an ALREADY signed-in visitor to the dashboard, decided once when auth first resolves.
  // The diagnostic on this page mints the account at email capture, so reacting to every
  // currentUser change pulled learners off their results mid-read (found on staging Sep 15).
  // Decided during render so the diagnostic unmounts in the same commit auth resolves, before its view event fires.
  const arrivedSignedIn = useArrivedSignedIn(Boolean(currentUser), isAuthLoading);
  useEffect(() => {
    if (arrivedSignedIn && !isStudentViewActive) {
      navigate('/dashboard');
    }
  }, [arrivedSignedIn, isStudentViewActive, navigate]);

  if (arrivedSignedIn && !isStudentViewActive) {
    return null;
  }

  const variant = diagnosticVariant();
  if (variant !== 'classic') {
    return (
      <>
        <Helmet>
          <meta property="og:title" content={HOME_OG_TITLE} />
          <meta property="og:description" content={SIX_TURN_META_DESCRIPTION} />
          <meta property="og:type" content="website" />
          <meta property="og:image" content={HOME_OG_IMAGE} />
          <meta property="og:image:width" content="1200" />
          <meta property="og:image:height" content="630" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:image" content={HOME_OG_IMAGE} />
          <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
        </Helmet>
        {variant === null ? (
          <DiagnosticPrerender
            canonicalPath="/"
            introPageTitle={HOME_TITLE}
            metaDescription={SIX_TURN_META_DESCRIPTION}
            belowSection={<LandingValueSections variant="six_turn" />}
          />
        ) : (
          <PublicDiagnosticShell
            gateSignedInArrivals
            canonicalPath="/"
            introPageTitle={HOME_TITLE}
            metaDescription={SIX_TURN_META_DESCRIPTION}
            heroSection={<SixTurnHero />}
            belowSection={<LandingValueSections variant="six_turn" />}
          />
        )}
      </>
    );
  }

  return (
    <TryAQuestion
      gateSignedInArrivals
      canonicalPath="/"
      introPageTitle={HOME_TITLE}
      metaDescription="Pass your ASWB exam with adaptive practice questions reviewed by a licensed clinical social worker. Take the free 6-question diagnostic and get your readiness score in 3 minutes. No account, no card. Your score and Pass Plan go to your inbox."
      ogTitle={HOME_OG_TITLE}
      ogImage={HOME_OG_IMAGE}
      structuredData={structuredData}
      heroSection={<HeroSection />}
      attachedHero
      introFootnote="Stuck on a tricky one? Every question here has been clinically reviewed by Pam, your clinical supervisor."
      belowIntroSection={<LandingValueSections />}
    />
  );
};

export default Landing;
